import { useDocumentTitle } from "@/lib/use-document-title";
import { useState, useMemo } from "react";
import { StaffLayout } from "@/components/StaffLayout";
import {
  useBenefitDefinitions, useBenefitMutations, type BenefitDefinition,
  usePartners, usePartnerMutations, type Partner,
  useStores,
} from "@/lib/data-hooks";
import { Plus, Pencil, Trash2, X, Star, Tag, ArrowUpDown } from "lucide-react";
import { IconPicker } from "@/components/IconPicker";
import { useConfirm } from "@/components/ConfirmDialog";

type TabKey = "benefits" | "partners";

function AdminPage() {
  useDocumentTitle("Admin — ÅF-Portal");
  const [tab, setTab] = useState<TabKey>("benefits");
  const tabs: { key: TabKey; label: string; icon: any }[] = [
    { key: "benefits", label: "Förmåner", icon: Star },
    { key: "partners", label: "Partners", icon: Tag },
  ];

  return (
    <StaffLayout title="Admin">
      <p className="text-muted-foreground mb-6 max-w-2xl">Hantera förmåner och partners. Event hanteras under <strong>Event</strong> i menyn, och kundhantering sker under <strong>Kundregister</strong>.</p>

      <div className="flex flex-wrap gap-2 mb-6">
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-display transition ${tab === t.key ? "bg-[var(--color-primary)] text-white" : "bg-white border border-border text-foreground hover:bg-muted"}`}
          >
            <t.icon size={14} /> {t.label}
          </button>
        ))}
      </div>

      {tab === "benefits" && <BenefitsAdmin />}
      {tab === "partners" && <PartnersAdmin />}
    </StaffLayout>
  );
}


// ------- BENEFITS -------
type SortDir = "asc" | "desc";
type SortCol = "name" | "category" | "is_limited" | "activation_type" | "active" | "sort_order";

export function BenefitsAdmin() {
  const { data: benefits = [], isLoading } = useBenefitDefinitions();
  const { create, update, remove } = useBenefitMutations();
  const confirm = useConfirm();
  const [editing, setEditing] = useState<Partial<BenefitDefinition> | null>(null);
  const [sortCol, setSortCol] = useState<SortCol>("sort_order");
  const [sortDir, setSortDir] = useState<SortDir>("asc");

  const onSave = async (b: Partial<BenefitDefinition>) => {
    if (b.id) await update.mutateAsync(b as any);
    else await create.mutateAsync(b);
    setEditing(null);
  };

  const toggleSort = (col: SortCol) => {
    if (sortCol === col) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortCol(col);
      setSortDir("asc");
    }
  };

  const sorted = useMemo(() => {
    const arr = [...benefits];
    arr.sort((a, b) => {
      let cmp = 0;
      switch (sortCol) {
        case "name":
          cmp = (a.name ?? "").localeCompare(b.name ?? "", "sv");
          break;
        case "category":
          cmp = (a.category ?? "").localeCompare(b.category ?? "", "sv");
          break;
        case "is_limited":
          cmp = Number(a.is_limited) - Number(b.is_limited);
          break;
        case "activation_type":
          cmp = (a.activation_type ?? "").localeCompare(b.activation_type ?? "", "sv");
          break;
        case "active":
          cmp = Number(a.active) - Number(b.active);
          break;
        case "sort_order":
          cmp = (a.sort_order ?? 0) - (b.sort_order ?? 0);
          break;
      }
      return sortDir === "asc" ? cmp : -cmp;
    });
    return arr;
  }, [benefits, sortCol, sortDir]);

  const SortHeader = ({ col, label }: { col: SortCol; label: string }) => (
    <th
      className="px-4 py-3 cursor-pointer select-none hover:text-foreground transition"
      onClick={() => toggleSort(col)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        <ArrowUpDown
          size={12}
          className={sortCol === col ? "text-primary opacity-100" : "opacity-40"}
        />
      </span>
    </th>
  );

  return (
    <>
      <div className="flex justify-end mb-4">
        <button onClick={() => setEditing({ name: "", description: "", icon: "star", category: "unlimited", is_limited: false, limit_per_period: 1, period_days: 30, sort_order: 0, active: true, activation_type: "in_store" })} className="btn-pill !text-sm"><Plus size={14} /> Lägg till förmån</button>
      </div>
      {isLoading && <p className="text-sm text-muted-foreground">Laddar…</p>}
      <div className="card-soft overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted"><tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
            <SortHeader col="name" label="Namn" />
            <SortHeader col="category" label="Kategori" />
            <SortHeader col="is_limited" label="Begränsad" />
            <SortHeader col="activation_type" label="Aktivering" />
            <SortHeader col="active" label="Aktiv" />
            <th className="px-4 py-3 text-right">Åtgärder</th>
          </tr></thead>
          <tbody>
            {sorted.map((b) => (
              <tr key={b.id} className="border-t border-border">
                <td className="px-4 py-3"><div className="font-display font-semibold">{b.name}</div><div className="text-xs text-muted-foreground">{b.description}</div></td>
                <td className="px-4 py-3">{b.category}</td>
                <td className="px-4 py-3">{b.is_limited ? `${b.limit_per_period}× / månad` : "Nej"}</td>
                <td className="px-4 py-3">{b.activation_type === "booking" ? "Bokningsbar" : "I butik"}</td>
                <td className="px-4 py-3">{b.active ? "Ja" : "Nej"}</td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => setEditing(b)} aria-label="Redigera" className="p-1.5 rounded-lg border border-border mr-1 hover:bg-muted"><Pencil size={13} /></button>
                  <button onClick={async () => { if (await confirm({ title: `Ta bort "${b.name}"?`, destructive: true, confirmText: "Ta bort" })) remove.mutate(b.id); }} aria-label="Ta bort" className="p-1.5 rounded-lg border border-border text-destructive hover:bg-destructive/5"><Trash2 size={13} /></button>
                </td>
              </tr>
            ))}
            {!sorted.length && !isLoading && <tr><td colSpan={6} className="px-4 py-10 text-center text-muted-foreground">Inga förmåner upplagda.</td></tr>}
          </tbody>
        </table>
      </div>
      {editing && (
        <Modal title={editing.id ? "Redigera förmån" : "Ny förmån"} onClose={() => setEditing(null)}>
          <BenefitForm initial={editing} onSubmit={onSave} onCancel={() => setEditing(null)} />
        </Modal>
      )}
    </>
  );
}

function BenefitForm({ initial, onSubmit, onCancel }: { initial: Partial<BenefitDefinition>; onSubmit: (b: Partial<BenefitDefinition>) => void; onCancel: () => void }) {
  const [b, setB] = useState<Partial<BenefitDefinition>>(initial);
  const isLimited = b.category === "limited";

  const setCategory = (cat: string) => {
    setB({
      ...b,
      category: cat,
      is_limited: cat === "limited",
      limit_per_period: cat === "limited" ? (b.limit_per_period ?? 1) : 1,
      period_days: cat === "limited" ? (b.period_days ?? 30) : 30,
    });
  };

  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit(b); }} className="space-y-5">
      {/* 1. Namn */}
      <Field label="1. Namn *">
        <input required value={b.name ?? ""} onChange={(e) => setB({ ...b, name: e.target.value })} className="input-mini" placeholder="t.ex. 20% rabatt på reparationer" />
      </Field>

      {/* 2. Beskrivning */}
      <Field label="2. Beskrivning">
        <textarea rows={2} value={b.description ?? ""} onChange={(e) => setB({ ...b, description: e.target.value })} className="input-mini" placeholder="Kort beskrivning som visas för medlemmen" />
      </Field>

      {/* 3. Tag (ikon) */}
      <Field label="3. Tag (ikon)">
        <IconPicker value={b.icon} onChange={(v) => setB({ ...b, icon: v })} />
      </Field>

      {/* 4. Kategori */}
      <Field label="4. Kategori *">
        <div className="grid grid-cols-3 gap-2">
          {[
            { v: "unlimited", label: "Obegränsad", hint: "Alltid aktiv" },
            { v: "limited", label: "Begränsad", hint: "Per period" },
            { v: "extra", label: "Extra", hint: "Övrig förmån" },
          ].map((opt) => (
            <button key={opt.v} type="button" onClick={() => setCategory(opt.v)}
              className={`px-3 py-3 rounded-lg border text-left transition ${b.category === opt.v ? "border-primary bg-primary/5" : "border-border hover:border-primary/40"}`}>
              <div className="text-sm font-display font-semibold">{opt.label}</div>
              <div className="text-[11px] text-muted-foreground">{opt.hint}</div>
            </button>
          ))}
        </div>
      </Field>

      {/* Vidare steg endast om limited */}
      {isLimited && (
        <div className="space-y-4 p-4 rounded-xl border border-[var(--color-gold)]/40 bg-[var(--color-gold)]/5">
          <div className="text-xs font-display font-bold tracking-widest uppercase text-[var(--color-gold)]">Inställningar för begränsad förmån</div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="5. Antal dagar per period">
              <input type="number" min={1} value={b.period_days ?? 30} onChange={(e) => setB({ ...b, period_days: Number(e.target.value) })} className="input-mini" />
            </Field>
            <Field label="6. Antal användningar per period">
              <input type="number" min={1} value={b.limit_per_period ?? 1} onChange={(e) => setB({ ...b, limit_per_period: Number(e.target.value) })} className="input-mini" />
            </Field>
          </div>

          <Field label="7. Aktiveringssätt">
            <div className="grid grid-cols-2 gap-2">
              {[
                { v: "in_store", label: "Aktivering i butik", hint: "Personal bekräftar med kod" },
                { v: "booking", label: "Bokning", hint: "Kunden bokar tid" },
              ].map((opt) => (
                <button key={opt.v} type="button" onClick={() => setB({ ...b, activation_type: opt.v as any })}
                  className={`px-3 py-3 rounded-lg border text-left transition ${(b.activation_type ?? "in_store") === opt.v ? "border-primary bg-white" : "border-border hover:border-primary/40 bg-white/50"}`}>
                  <div className="text-sm font-display font-semibold">{opt.label}</div>
                  <div className="text-[11px] text-muted-foreground">{opt.hint}</div>
                </button>
              ))}
            </div>
          </Field>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3 pt-2 border-t border-border">
        <Field label="Sorteringsordning">
          <input type="number" value={b.sort_order ?? 0} onChange={(e) => setB({ ...b, sort_order: Number(e.target.value) })} className="input-mini" />
        </Field>
        <label className="flex items-end gap-2 text-sm pb-2"><input type="checkbox" checked={b.active ?? true} onChange={(e) => setB({ ...b, active: e.target.checked })} /> Aktiv</label>
      </div>

      <FormFooter onCancel={onCancel} />
    </form>
  );
}

// ------- PARTNERS -------
function PartnersAdmin() {
  const { data: partners = [], isLoading } = usePartners();
  const { create, update, remove } = usePartnerMutations();
  const confirm = useConfirm();
  const [editing, setEditing] = useState<Partial<Partner> | null>(null);
  const onSave = async (p: Partial<Partner>) => {
    if (p.id) await update.mutateAsync(p as any); else await create.mutateAsync(p);
    setEditing(null);
  };
  return (
    <>
      <div className="flex justify-end mb-4"><button onClick={() => setEditing({ name: "", description: "", discount_label: "", cta_url: "", sort_order: 0, active: true })} className="btn-pill !text-sm"><Plus size={14} /> Lägg till partner</button></div>
      {isLoading && <p className="text-sm text-muted-foreground">Laddar…</p>}
      <div className="card-soft overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted"><tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
            <th className="px-4 py-3">Namn</th><th className="px-4 py-3">Rabatt</th><th className="px-4 py-3">Webb</th><th className="px-4 py-3">Aktiv</th><th className="px-4 py-3 text-right">Åtgärder</th>
          </tr></thead>
          <tbody>
            {partners.map((p) => (
              <tr key={p.id} className="border-t border-border">
                <td className="px-4 py-3"><div className="font-display font-semibold">{p.name}</div><div className="text-xs text-muted-foreground">{p.description}</div></td>
                <td className="px-4 py-3">{p.discount_label}</td>
                <td className="px-4 py-3 text-xs text-blue-600 truncate max-w-[200px]">{p.cta_url}</td>
                <td className="px-4 py-3">{p.active ? "Ja" : "Nej"}</td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => setEditing(p)} aria-label="Redigera partner" className="p-1.5 rounded-lg border border-border mr-1 hover:bg-muted"><Pencil size={13} /></button>
                  <button onClick={async () => { if (await confirm({ title: `Ta bort "${p.name}"?`, destructive: true, confirmText: "Ta bort" })) remove.mutate(p.id); }} aria-label="Ta bort partner" className="p-1.5 rounded-lg border border-border text-destructive hover:bg-destructive/5"><Trash2 size={13} /></button>
                </td>
              </tr>
            ))}
            {!partners.length && !isLoading && <tr><td colSpan={5} className="px-4 py-10 text-center text-muted-foreground">Inga partners upplagda.</td></tr>}
          </tbody>
        </table>
      </div>
      {editing && (
        <Modal title={editing.id ? "Redigera partner" : "Ny partner"} onClose={() => setEditing(null)}>
          <PartnerForm initial={editing} onSubmit={onSave} onCancel={() => setEditing(null)} />
        </Modal>
      )}
    </>
  );
}

function PartnerForm({ initial, onSubmit, onCancel }: { initial: Partial<Partner>; onSubmit: (p: Partial<Partner>) => void; onCancel: () => void }) {
  const [p, setP] = useState<Partial<Partner>>(initial);
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit(p); }} className="space-y-4">
      <Field label="Namn *"><input required value={p.name ?? ""} onChange={(e) => setP({ ...p, name: e.target.value })} className="input-mini" /></Field>
      <Field label="Beskrivning"><textarea rows={2} value={p.description ?? ""} onChange={(e) => setP({ ...p, description: e.target.value })} className="input-mini" /></Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Rabatttext"><input value={p.discount_label ?? ""} onChange={(e) => setP({ ...p, discount_label: e.target.value })} className="input-mini" placeholder="20%" /></Field>
        <Field label="Sorteringsordning"><input type="number" value={p.sort_order ?? 0} onChange={(e) => setP({ ...p, sort_order: Number(e.target.value) })} className="input-mini" /></Field>
      </div>
      <Field label="Webbadress"><input type="url" value={p.cta_url ?? ""} onChange={(e) => setP({ ...p, cta_url: e.target.value })} className="input-mini" placeholder="https://..." /></Field>
      <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={p.active ?? true} onChange={(e) => setP({ ...p, active: e.target.checked })} /> Aktiv</label>
      <FormFooter onCancel={onCancel} />
    </form>
  );
}



// ------- shared bits -------
function Modal({ title, children, onClose }: { title: string; children: React.ReactNode; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border sticky top-0 bg-white">
          <h3 className="font-display font-bold text-lg">{title}</h3>
          <button onClick={onClose} aria-label="Stäng" className="p-1.5 rounded-lg hover:bg-muted"><X size={18} /></button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <label className="block"><span className="block text-xs font-display font-semibold text-foreground mb-1.5">{label}</span>{children}</label>;
}

function FormFooter({ onCancel }: { onCancel: () => void }) {
  return (
    <div className="flex gap-3 pt-2">
      <button type="button" onClick={onCancel} className="flex-1 py-2.5 rounded-full border border-border text-sm font-display hover:bg-muted transition">Avbryt</button>
      <button type="submit" className="flex-1 py-2.5 rounded-full bg-[var(--color-primary)] text-white text-sm font-display font-semibold hover:opacity-90 transition">Spara</button>
    </div>
  );
}

// Inline class helper för formulärfält
// Vi använder inline klass via globala input-mini definierad nedan
// (fallback om saknas: använder samma stilar som befintliga input-fält)

export default AdminPage;
